import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import "./sip-calculator.css";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const formatINR = (amount) =>
  Number(amount.toFixed(0)).toLocaleString("en-IN");

function computeEligibility(
  monthlyIncome,
  monthlyExpenses,
  interestRate,
  loanTenure
) {
  if (
    monthlyIncome <= 0 ||
    monthlyExpenses < 0 ||
    interestRate <= 0 ||
    loanTenure <= 0
  ) {
    return null;
  }

  const netIncome = monthlyIncome - monthlyExpenses;
  if (netIncome <= 0) return null;

  const monthlyInterestRate = interestRate / 12 / 100;
  const totalMonths = loanTenure * 12;

  const emi =
    (netIncome *
      monthlyInterestRate *
      Math.pow(1 + monthlyInterestRate, totalMonths)) /
    (Math.pow(1 + monthlyInterestRate, totalMonths) - 1);

  const eligibleLoanAmount = emi * totalMonths;

  return {
    netIncome: Number(netIncome.toFixed(0)),
    emi: Number(emi.toFixed(0)),
    eligibleLoanAmount: Number(eligibleLoanAmount.toFixed(0)),
    tenureYears: loanTenure,
  };
}

export default function LoanEligibilityEstimator() {
  const [monthlyIncome, setMonthlyIncome] = useState(50000);
  const [monthlyExpenses, setMonthlyExpenses] = useState(20000);
  const [interestRate, setInterestRate] = useState(8);
  const [loanTenure, setLoanTenure] = useState(20);

  const results = useMemo(
    () =>
      computeEligibility(
        monthlyIncome,
        monthlyExpenses,
        interestRate,
        loanTenure
      ),
    [monthlyIncome, monthlyExpenses, interestRate, loanTenure]
  );

  const doughnutData = results && {
    labels: ["Eligible Loan Amount", "Net Monthly Income"],
    datasets: [
      {
        data: [results.eligibleLoanAmount, results.netIncome],
        backgroundColor: ["#139bb2", "#a8e4ec"],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
          color: "#64748b",
          font: { size: 12, weight: "600" },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ₹${formatINR(ctx.parsed)}`,
        },
      },
    },
  };

  const barData = results && {
    labels: ["Estimated EMI", "Net Monthly Income"],
    datasets: [
      {
        label: "Amount",
        data: [results.emi, results.netIncome],
        backgroundColor: ["#139bb2", "#a8e4ec"],
        borderRadius: 8,
        barThickness: 56,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => `₹${formatINR(ctx.parsed.y)}`,
        },
      },
      legend: { display: false },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#64748b", font: { size: 12, weight: "600" } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#eef2f5" },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
          callback: (v) => `₹${formatINR(v)}`,
        },
      },
    },
  };

  return (
    <div className="sip-page">
      <section className="sip-hero">
        <div className="sip-hero-blob" aria-hidden="true" />
        <div className="sip-hero-inner">
          <p className="sip-eyebrow">LOAN ELIGIBILITY</p>
          <h1 className="sip-hero-title">
            Estimate How Much Loan You Can <span>Qualify For</span>
          </h1>
          <p className="sip-hero-lead">
            Enter your income, expenses, interest rate, and tenure to get a
            quick estimate of your eligible loan amount and affordable EMI.
          </p>
        </div>
      </section>

      <section className="sip-calc">
        <div className="sip-calc-inner">
          <div className="sip-calc-grid">
            <div className="sip-panel">
              <h2 className="sip-panel-title">Your Financial Details</h2>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="elig-income">Monthly Income</label>
                  <div className="sip-field-value sip-field-value--wide">
                    <span>₹</span>
                    <input
                      id="elig-income"
                      type="number"
                      min={10000}
                      max={1000000}
                      step={1000}
                      value={monthlyIncome}
                      onChange={(e) =>
                        setMonthlyIncome(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={10000}
                  max={500000}
                  step={1000}
                  value={Math.min(monthlyIncome, 500000)}
                  onChange={(e) => setMonthlyIncome(Number(e.target.value))}
                  aria-label="Monthly income slider"
                />
                <div className="sip-range-ends">
                  <span>₹10,000</span>
                  <span>₹5,00,000</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="elig-expenses">Monthly Expenses</label>
                  <div className="sip-field-value sip-field-value--wide">
                    <span>₹</span>
                    <input
                      id="elig-expenses"
                      type="number"
                      min={0}
                      max={900000}
                      step={1000}
                      value={monthlyExpenses}
                      onChange={(e) =>
                        setMonthlyExpenses(Number(e.target.value) || 0)
                      }
                    />
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={0}
                  max={300000}
                  step={1000}
                  value={Math.min(monthlyExpenses, 300000)}
                  onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
                  aria-label="Monthly expenses slider"
                />
                <div className="sip-range-ends">
                  <span>₹0</span>
                  <span>₹3,00,000</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="elig-rate">Interest Rate (p.a.)</label>
                  <div className="sip-field-value">
                    <input
                      id="elig-rate"
                      type="number"
                      min={1}
                      max={30}
                      step={0.1}
                      value={interestRate}
                      onChange={(e) =>
                        setInterestRate(Number(e.target.value) || 0)
                      }
                    />
                    <span>%</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1}
                  max={30}
                  step={0.1}
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  aria-label="Interest rate slider"
                />
                <div className="sip-range-ends">
                  <span>1%</span>
                  <span>30%</span>
                </div>
              </div>

              <div className="sip-field">
                <div className="sip-field-head">
                  <label htmlFor="elig-tenure">Loan Tenure</label>
                  <div className="sip-field-value">
                    <input
                      id="elig-tenure"
                      type="number"
                      min={1}
                      max={30}
                      step={1}
                      value={loanTenure}
                      onChange={(e) =>
                        setLoanTenure(Number(e.target.value) || 0)
                      }
                    />
                    <span>Yr</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="sip-range"
                  min={1}
                  max={30}
                  step={1}
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(Number(e.target.value))}
                  aria-label="Loan tenure slider"
                />
                <div className="sip-range-ends">
                  <span>1 Yr</span>
                  <span>30 Yr</span>
                </div>
              </div>

              <div className="sip-cta-row">
                <Link to="/loan" className="sip-btn sip-btn--primary">
                  Explore Loan Options
                </Link>
                <Link to="/emi-calculator" className="sip-btn sip-btn--ghost">
                  Open EMI Calculator
                </Link>
              </div>
            </div>

            <div className="sip-panel sip-panel--results">
              <h2 className="sip-panel-title">Eligibility Estimate</h2>

              {results ? (
                <>
                  <div className="sip-results-top">
                    <div className="sip-donut-wrap">
                      <Doughnut data={doughnutData} options={doughnutOptions} />
                    </div>
                    <div className="sip-stats">
                      <div className="sip-stat">
                        <span className="sip-stat-label">Net Monthly Income</span>
                        <strong>₹{formatINR(results.netIncome)}</strong>
                      </div>
                      <div className="sip-stat">
                        <span className="sip-stat-label">Estimated EMI</span>
                        <strong className="sip-stat--teal">
                          ₹{formatINR(results.emi)}
                        </strong>
                      </div>
                      <div className="sip-stat sip-stat--total">
                        <span className="sip-stat-label">
                          Eligible Loan Amount
                        </span>
                        <strong>
                          ₹{formatINR(results.eligibleLoanAmount)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <p className="sip-summary">
                    Based on income of{" "}
                    <strong>₹{formatINR(monthlyIncome)}</strong> and expenses of{" "}
                    <strong>₹{formatINR(monthlyExpenses)}</strong>, you may
                    afford an EMI of <strong>₹{formatINR(results.emi)}</strong>{" "}
                    over <strong>{loanTenure} years</strong>, with an estimated
                    eligible loan amount of{" "}
                    <strong>₹{formatINR(results.eligibleLoanAmount)}</strong>.
                  </p>
                </>
              ) : (
                <p className="sip-empty">
                  Enter income higher than expenses to see your eligibility
                  estimate.
                </p>
              )}
            </div>
          </div>

          {results && (
            <div className="sip-panel sip-panel--chart">
              <div className="sip-chart-head">
                <h2 className="sip-panel-title">EMI vs Net Income</h2>
                <p>How your estimated EMI compares with disposable income</p>
              </div>
              <div className="sip-bar-wrap" style={{ height: 320 }}>
                <Bar data={barData} options={barOptions} />
              </div>
            </div>
          )}

          <div className="sip-related">
            <p>Try our other calculators</p>
            <div className="sip-related-links">
              <Link to="/emi-calculator">EMI</Link>
              <Link to="/tenure-optimizer">Tenure Optimizer</Link>
              <Link to="/SipCalculator">SIP</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
